import { cn } from '@/lib/utils';
import { AlertTriangle, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Empty / error placeholder block for async surfaces. Centered icon + title +
 * optional description and action. For loading states prefer skeletons inline.
 */
export function AsyncState({
  icon: Icon,
  title,
  description,
  action,
  tone = 'muted',
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  tone?: 'muted' | 'danger';
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-4 py-12 text-center', className)}
    >
      {Icon ? (
        <Icon
          className={cn(
            'mb-4 size-10',
            tone === 'danger' ? 'text-fin-danger' : 'text-fin-gray-500 opacity-70'
          )}
          aria-hidden
        />
      ) : null}
      <h3 className='text-fin-white text-base font-medium'>{title}</h3>
      {description ? (
        <p className='text-fin-gray-500 mt-1 max-w-sm text-sm'>{description}</p>
      ) : null}
      {action ? <div className='mt-4'>{action}</div> : null}
    </div>
  );
}

/** Error variant of {@link AsyncState} with a default warning icon. */
export function ErrorState({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <AsyncState
      icon={AlertTriangle}
      tone='danger'
      title={title}
      description={description}
      action={action}
      className={className}
    />
  );
}
